import React from 'react';
import { History, Crown, Download } from 'lucide-react';
import type { SessionHistoryResponse } from '../../lib/sessions';
import { downloadSessionExport, fetchSessionHistory } from '../../lib/sessions';

export function formatFocusTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

export function formatStage(stage: string): string {
  return stage
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

export function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(date, today)) return 'Today';
  if (sameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface SessionHistorySectionProps {
  onUpgrade?: () => void;
}

export function SessionHistorySection({ onUpgrade }: SessionHistorySectionProps) {
  const [open, setOpen] = React.useState(false);
  const [history, setHistory] = React.useState<SessionHistoryResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Lazy-load: fetch once, the first time the section is expanded.
  React.useEffect(() => {
    if (!open || history || error) return;
    fetchSessionHistory()
      .then(setHistory)
      .catch(() => setError('Could not load history'));
  }, [open, history, error]);

  const daily = history?.daily ?? [];
  const recent = (history?.sessions ?? []).slice(0, 8);
  const totalFocusSeconds = daily.reduce((sum, day) => sum + day.focusSeconds, 0);
  const totalFocusCount = daily.reduce((sum, day) => sum + day.focusCount, 0);

  // Last 7 days, oldest first, for the mini bar chart.
  const chartDays = daily.slice(0, 7).reverse();
  const maxSeconds = Math.max(1, ...chartDays.map((day) => day.focusSeconds));

  return (
    <div className="border-t border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setOpen((value) => !value)}
        className="flex items-center w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
        aria-expanded={open}
      >
        <div className="flex items-center flex-1">
          <History className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          <span className="ml-3 font-medium text-gray-900 dark:text-white">History</span>
        </div>
        <div className={`transform transition-transform ${open ? 'rotate-180' : ''}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>

      {open && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 space-y-3">
          {error && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
          )}
          {!error && !history && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          )}
          {history && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700 dark:text-gray-300">
                  Focus sessions{history.window_days ? ` (last ${history.window_days} days)` : ''}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {totalFocusCount} · {formatFocusTime(totalFocusSeconds)}
                </span>
              </div>

              {chartDays.length > 0 && (
                <div className="flex items-end justify-between gap-1 h-14 pt-1" role="img" aria-label="Focus minutes per day, last 7 days">
                  {chartDays.map((day) => {
                    const heightPct = Math.max(4, Math.round((day.focusSeconds / maxSeconds) * 100));
                    const isToday = day.day === daily[0]?.day;
                    return (
                      <div key={day.day} className="flex-1 flex flex-col items-center justify-end h-full" title={`${formatDay(day.day)}: ${formatFocusTime(day.focusSeconds)}`}>
                        <div
                          className={`w-full rounded-sm ${isToday ? 'bg-indigo-500' : 'bg-indigo-300 dark:bg-indigo-700'}`}
                          style={{ height: `${heightPct}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {daily.length > 0 && (
                <div className="space-y-1">
                  {daily.slice(0, 7).map((day) => (
                    <div key={day.day} className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                      <span>{formatDay(day.day)}</span>
                      <span>
                        {day.focusCount} focus · {formatFocusTime(day.focusSeconds)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {recent.length > 0 && (
                <div className="space-y-1 border-t border-gray-200 dark:border-gray-700 pt-2">
                  {recent.map((session) => (
                    <div key={session.id} className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                      <span className="capitalize">
                        {formatStage(session.stage)}
                        {session.label ? ` — ${session.label}` : ''}
                      </span>
                      <span>{formatFocusTime(session.duration_seconds)}</span>
                    </div>
                  ))}
                </div>
              )}

              {daily.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No sessions yet — complete a focus session to start your history.
                </p>
              )}

              {history.tier === 'free' && onUpgrade && (
                <button
                  onClick={onUpgrade}
                  className="w-full flex items-center justify-center gap-2 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline pt-1"
                >
                  <Crown className="w-3.5 h-3.5" />
                  Upgrade to keep your full history forever
                </button>
              )}

              {history.export_enabled && (
                <div className="flex justify-center gap-3 pt-1 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => downloadSessionExport('csv').catch(() => {})}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => downloadSessionExport('json').catch(() => {})}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  >
                    <Download className="w-3.5 h-3.5" />
                    JSON
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
